import React, { useEffect, useState } from "react";
import {
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Typography,
} from "@material-ui/core";
import { Resource } from "react-admin";
import { useTokenOps } from "../components/TokenContext";
import {
  CARBON_REFERENCE_CATALOGUES,
  fetchCarbonReferenceData,
  localizedReferenceLabel,
} from "./corporateCarbon/referenceData";
import {
  CarbonAdminPage,
  CarbonDataTable,
  CarbonFilterBar,
  CarbonSelectInput,
} from "./corporateCarbon/CarbonAdminPage";

const DEFAULT_CATALOGUE = "siteType";

export const CarbonReferenceDataViewer = ({ accessToken }) => {
  const tokenOps = useTokenOps();
  const currentAccessToken = accessToken || tokenOps.account?.accessToken;
  const [catalogue, setCatalogue] = useState(DEFAULT_CATALOGUE);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [referenceData, setReferenceData] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    if (!currentAccessToken) {
      setLoading(false);
      setError(
        new Error(
          "An active account token is required to load reference data.",
        ),
      );
      return () => {
        active = false;
      };
    }

    fetchCarbonReferenceData(currentAccessToken, catalogue, { includeRetired })
      .then((response) => {
        if (active) setReferenceData(response);
      })
      .catch((requestError) => {
        if (active) setError(requestError);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [catalogue, currentAccessToken, includeRetired]);

  return (
    <CarbonAdminPage
      title="Carbon reference data"
      description="Read-only catalogue values used by corporate carbon accounting."
      variant="list"
    >
        <CarbonFilterBar label="Reference data filters">
          <CarbonSelectInput
            choices={CARBON_REFERENCE_CATALOGUES.map(([id, name]) => ({ id, name }))}
            emptyText={null}
            id="carbon-reference-catalogue"
            label="Catalogue"
            name="catalogue"
            value={catalogue}
            onChange={(event) => setCatalogue(event.target.value)}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeRetired}
                onChange={(event) => setIncludeRetired(event.target.checked)}
                name="includeRetired"
              />
            }
            label="Include retired entries"
          />
        </CarbonFilterBar>
        {loading && (
          <div role="status" aria-live="polite">
            <CircularProgress size={20} aria-hidden="true" /> Loading reference
            data…
          </div>
        )}
        {!loading && error && (
          <Typography role="alert" color="error">
            {error.message}
          </Typography>
        )}
        {!loading && !error && referenceData.data.length === 0 && (
          <Typography>
            No reference data entries match this catalogue.
          </Typography>
        )}
        {!loading && !error && referenceData.data.length > 0 && (
          <>
            <Typography color="textSecondary" paragraph>
              {referenceData.total} entries
            </Typography>
            <CarbonDataTable label="Reference data entries">
              <thead>
                <tr>
                  <th scope="col" align="left">
                    Code
                  </th>
                  <th scope="col" align="left">
                    Label
                  </th>
                  <th scope="col" align="left">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {referenceData.data.map((record) => (
                  <tr key={record.id}>
                    <td>{record.code}</td>
                    <td>{localizedReferenceLabel(record.label)}</td>
                    <td>{record.retired ? "Retired" : "Active"}</td>
                  </tr>
                ))}
              </tbody>
            </CarbonDataTable>
          </>
        )}
    </CarbonAdminPage>
  );
};

const CarbonReferenceDataList = () => <CarbonReferenceDataViewer />;

const carbonReferenceDataResource = () => (
  <Resource
    name="carbonReferenceData"
    options={{
      label: "Reference data catalogues",
      menuGroup: "Bcome Carbon",
    }}
    list={CarbonReferenceDataList}
  />
);

export default carbonReferenceDataResource;
